import { useState, useEffect, useCallback } from 'react'
import { listUsers } from '../api/admin'
import type { AdminUser } from '../types'
import { LoadingSpinner } from '../components/LoadingSpinner'
import styles from './AdminUsersTable.module.css'

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('es-CL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

export function AdminUsersTable() {
  const [users, setUsers] = useState<AdminUser[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const LIMIT = 20

  const load = useCallback(async (p: number) => {
    setLoading(true)
    setError(null)
    try {
      const resp = await listUsers(p, LIMIT)
      setUsers(resp.items)
      setTotal(resp.total)
    } catch {
      setError('No se pudieron cargar los usuarios.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load(page) }, [load, page])

  const totalPages = Math.max(1, Math.ceil(total / LIMIT))

  if (loading) {
    return (
      <div className={styles.centered}>
        <LoadingSpinner size="md" message="Cargando usuarios..." />
      </div>
    )
  }

  if (error) {
    return <p className={styles.error}>{error}</p>
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.tableScroll}>
        <table className={styles.table} aria-label="Tabla de usuarios">
          <thead>
            <tr>
              <th scope="col">Email</th>
              <th scope="col">Nombre</th>
              <th scope="col">Plan</th>
              <th scope="col">Registrado</th>
              <th scope="col">Último login</th>
              <th scope="col">Verificado</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan={6} className={styles.empty}>No hay usuarios registrados.</td>
              </tr>
            ) : (
              users.map((u) => (
                <tr key={u.id}>
                  <td data-label="Email">{u.email}</td>
                  <td data-label="Nombre">{u.full_name ?? '—'}</td>
                  <td data-label="Plan">
                    <span className={`${styles.badge} ${u.plan === 'pro' ? styles.badgePro : styles.badgeFree}`}>
                      {u.plan}
                    </span>
                  </td>
                  <td data-label="Registrado">{formatDate(u.created_at)}</td>
                  <td data-label="Último login">{formatDate(u.last_login)}</td>
                  <td data-label="Verificado">
                    <span
                      className={`${styles.badge} ${u.email_verified ? styles.badgeSuccess : styles.badgeWarning}`}
                      aria-label={u.email_verified ? 'Verificado' : 'Sin verificar'}
                    >
                      {u.email_verified ? 'Sí' : 'No'}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className={styles.pagination} role="navigation" aria-label="Paginación usuarios">
          <button
            className={styles.pageBtn}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            aria-label="Página anterior"
          >
            ← Anterior
          </button>
          <span className={styles.pageInfo}>
            Página {page} de {totalPages} ({total} usuarios)
          </span>
          <button
            className={styles.pageBtn}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            aria-label="Página siguiente"
          >
            Siguiente →
          </button>
        </div>
      )}
    </div>
  )
}
